import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Eye, FileCode2, Printer, ReceiptText, Send } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { notificar } from '@/shared/ui/avisos';
import { BotonDeFila } from '@/shared/ui/BotonDeFila';
import { CampoDeBusqueda } from '@/shared/ui/CampoDeBusqueda';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { useValorDiferido } from '@/shared/ui/useValorDiferido';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { PanelDeLaRetencion } from './PanelDeLaRetencion';
import type { ListaDeRetenciones, Retencion } from './contrato';

/** Cómo quedó ante el SRI, con el color que corresponde a cada situación. */
const SITUACIONES: Record<string, { rotulo: string; estilo: string }> = {
  interna: { rotulo: 'Sin enviar', estilo: 'bg-armazon-100 text-armazon-600' },
  firmada: { rotulo: 'Firmada', estilo: 'bg-blue-50 text-blue-700' },
  enviada: { rotulo: 'Enviada', estilo: 'bg-amber-50 text-amber-700' },
  autorizada: { rotulo: 'Autorizada', estilo: 'bg-marca-50 text-marca-700' },
  rechazada: { rotulo: 'Rechazada', estilo: 'bg-red-50 text-alerta' },
};

/**
 * Los comprobantes de retención que EPAGAL entrega a sus proveedores.
 *
 * <p>Al comprar, la entidad retiene una parte del impuesto a la renta y del IVA de la factura y
 * se la entrega al SRI en nombre del proveedor. <b>Para el proveedor no es un papel más</b>: sin
 * este comprobante no puede acreditarse ese valor en su declaración.
 */
export function PaginaRetenciones() {
  const [texto, setTexto] = useState('');
  const [situacion, setSituacion] = useState('');
  const [periodo, setPeriodo] = useState('');
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);
  const [viendo, setViendo] = useState<Retencion | null>(null);
  const [anulando, setAnulando] = useState<Retencion | null>(null);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState<number | null>(null);

  const clienteDeConsultas = useQueryClient();
  const { puede } = useSesion();
  const puedeEmitir = puede('compras.retenciones.emitir');
  const puedeAnular = puede('compras.retenciones.anular');

  const buscado = useValorDiferido(texto.trim());

  const listado = useQuery({
    queryKey: ['retenciones', buscado, situacion, periodo, pagina, tamanio],
    queryFn: () =>
      api.get<ListaDeRetenciones>('/retenciones', {
        query: { buscar: buscado, situacionSri: situacion, periodoFiscal: periodo, pagina, tamanio },
      }),
    placeholderData: (anterior) => anterior,
  });

  const emitir = useMutation({
    mutationFn: ({ id, punto }: { id: number; punto: number }) =>
      api.post<Retencion>(`/retenciones/${id}/emitir?puntoEmision=${punto}`, {}),
    onSuccess: (r) => {
      setEnviando(null);
      // El SRI puede aceptar, rechazar o no contestar, y las tres son respuestas válidas: lo
      // que no puede es decirse «listo» cuando quedó rechazada.
      if (r.situacionSri === 'autorizada') {
        notificar.exito(`Retención ${r.numero} autorizada`, 'Listo');
      } else if (r.situacionSri === 'rechazada') {
        notificar.error(r.mensajeSri ?? 'El SRI la rechazó', 'Rechazada');
      } else {
        notificar.advertencia(r.mensajeSri ?? 'Sin respuesta del SRI', 'Queda pendiente');
      }
      void clienteDeConsultas.invalidateQueries({ queryKey: ['retenciones'] });
    },
    onError: (causa) => {
      setEnviando(null);
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo emitir');
    },
  });

  const anular = useMutation({
    mutationFn: () => api.post<Retencion>(`/retenciones/${anulando?.id}/anular`, { motivo }),
    onSuccess: () => {
      notificar.exito('Retención anulada');
      setAnulando(null);
      setMotivo('');
      void clienteDeConsultas.invalidateQueries({ queryKey: ['retenciones'] });
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo anular'),
  });

  /** El papel para el proveedor: se abre en otra pestaña, que es como se revisa antes de enviar. */
  async function abrirRide(r: Retencion) {
    try {
      const pdf = await api.descargar(`/retenciones/${r.id}/ride.pdf`);
      if (!pdf) {
        notificar.advertencia('Esa retención todavía no tiene comprobante');
        return;
      }
      window.open(URL.createObjectURL(pdf), '_blank');
    } catch (causa) {
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo abrir el PDF');
    }
  }

  async function descargarXml(r: Retencion) {
    try {
      const xml = await api.descargar(`/retenciones/${r.id}/comprobante.xml`);
      if (!xml) {
        notificar.advertencia('Esa retención todavía no tiene XML');
        return;
      }
      const enlace = document.createElement('a');
      enlace.href = URL.createObjectURL(xml);
      enlace.download = `retencion-${r.numero ?? r.id}.xml`;
      enlace.click();
      URL.revokeObjectURL(enlace.href);
    } catch (causa) {
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo descargar el XML');
    }
  }

  const columnas: Columna<Retencion>[] = [
    {
      clave: 'numero',
      encabezado: 'Nº retención',
      contenido: (r) => (
        <span className="block">
          <span className="block font-mono text-xs font-semibold text-armazon-700">
            {r.numero ?? <span className="text-armazon-400">SIN EMITIR</span>}
          </span>
          <span className="block text-[10px] uppercase text-armazon-400">{r.periodoFiscal}</span>
        </span>
      ),
    },
    {
      clave: 'proveedor',
      encabezado: 'Proveedor',
      contenido: (r) => (
        <span className="block">
          <span className="block truncate font-medium text-armazon-700">{r.proveedor}</span>
          <span className="block font-mono text-[10px] text-armazon-400">{r.identificacion}</span>
        </span>
      ),
    },
    {
      clave: 'compra',
      encabezado: 'Retiene de',
      contenido: (r) => (
        <span className="font-mono text-xs text-armazon-600">{r.comprobanteNumero}</span>
      ),
    },
    {
      clave: 'total',
      encabezado: 'Retenido',
      alinear: 'derecha',
      contenido: (r) => (
        <span className="importe font-semibold text-armazon-800">
          ${r.totalRetenido.toFixed(2)}
        </span>
      ),
    },
    {
      clave: 'situacion',
      encabezado: 'Ante el SRI',
      contenido: (r) => {
        const s = SITUACIONES[r.situacionSri] ?? {
          rotulo: r.situacionSri,
          estilo: 'bg-armazon-100 text-armazon-600',
        };
        return (
          <span className="block">
            <span
              className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold uppercase ${s.estilo}`}
            >
              {r.estado === 'anulada' ? 'Anulada' : s.rotulo}
            </span>
            {r.numeroAutorizacion && (
              <span className="mt-0.5 block font-mono text-[10px] text-armazon-400">
                {r.numeroAutorizacion.slice(0, 20)}…
              </span>
            )}
          </span>
        );
      },
    },
    {
      clave: 'acciones',
      encabezado: '',
      alinear: 'derecha',
      contenido: (r) => (
        <span className="flex justify-end gap-1">
          <BotonDeFila icono={Eye} titulo="Ver el detalle" alPulsar={() => setViendo(r)} />
          {puedeEmitir && r.estado !== 'anulada' && r.situacionSri !== 'autorizada' && (
            <BotonDeFila
              icono={Send}
              titulo="Enviar al SRI"
              alPulsar={() => setEnviando(r.id)}
              ocupado={emitir.isPending && enviando === r.id}
            />
          )}
          {r.tieneXml && (
            <>
              <BotonDeFila
                icono={Printer}
                titulo="Abrir el comprobante impreso"
                alPulsar={() => void abrirRide(r)}
              />
              <BotonDeFila
                icono={FileCode2}
                titulo="Descargar el XML"
                alPulsar={() => void descargarXml(r)}
              />
            </>
          )}
          {puedeAnular && r.estado !== 'anulada' && (
            <BotonDeFila icono={Ban} titulo="Anular" alPulsar={() => setAnulando(r)} />
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <EncabezadoDePagina
        icono={ReceiptText}
        color="indigo"
        titulo="Comprobantes de retención"
        subtitulo="Lo que EPAGAL retiene a sus proveedores y les entrega"
      />

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <CampoDeBusqueda
          valor={texto}
          alCambiar={(v) => {
            setTexto(v);
            setPagina(0);
          }}
          marcador="Buscar por proveedor, identificación o número"
        />
        <select
          value={situacion}
          onChange={(e) => {
            setSituacion(e.target.value);
            setPagina(0);
          }}
          aria-label="Situación ante el SRI"
          className="h-10 rounded-xl border border-armazon-300 bg-white px-3 text-sm text-armazon-700"
        >
          <option value="">Todas</option>
          {Object.entries(SITUACIONES).map(([clave, s]) => (
            <option key={clave} value={clave}>
              {s.rotulo}
            </option>
          ))}
        </select>
        <input
          value={periodo}
          onChange={(e) => {
            setPeriodo(e.target.value);
            setPagina(0);
          }}
          placeholder="MM/AAAA"
          aria-label="Período fiscal"
          className="importe h-10 w-28 rounded-xl border border-armazon-300 bg-white px-3 text-sm text-armazon-700"
        />
      </div>

      <div className="mt-4">
        <Tabla
          datos={listado.data}
          cargando={listado.isPending}
          columnas={columnas}
          tamanio={tamanio}
          alCambiarPagina={setPagina}
          alCambiarTamanio={(t) => {
            setTamanio(t);
            setPagina(0);
          }}
          entidad="retenciones"
          iconoVacio={ReceiptText}
          mensajeVacio="Todavía no se ha emitido ninguna retención"
        />
      </div>

      {viendo && <PanelDeLaRetencion retencion={viendo} alCerrar={() => setViendo(null)} />}

      {/* El punto de emisión decide el número del comprobante, y un comprobante mal numerado
          ante el SRI no se arregla: por eso se pregunta en vez de suponerlo. */}
      {enviando !== null && (
        <ConfirmarEnvio
          alCancelar={() => setEnviando(null)}
          alEnviar={(punto) => emitir.mutate({ id: enviando, punto })}
          ocupado={emitir.isPending}
        />
      )}

      {anulando && (
        <ConfirmarAnulacion
          motivo={motivo}
          alEscribir={setMotivo}
          alCancelar={() => {
            setAnulando(null);
            setMotivo('');
          }}
          alAnular={() => anular.mutate()}
          ocupado={anular.isPending}
        />
      )}
    </>
  );
}

function ConfirmarEnvio({
  alCancelar,
  alEnviar,
  ocupado,
}: {
  alCancelar: () => void;
  alEnviar: (punto: number) => void;
  ocupado: boolean;
}) {
  const [punto, setPunto] = useState('');

  const puntos = useQuery({
    queryKey: ['puntos-emision'],
    queryFn: () => api.get<{ id: number; codigo: string; nombre: string }[]>('/puntos-emision'),
  });

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-armazon-950/60 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <h2 className="text-sm font-bold uppercase tracking-wide text-armazon-800">
          Enviar al SRI
        </h2>
        <p className="mt-1 text-[11px] uppercase leading-relaxed tracking-wide text-armazon-500">
          Se numera, se firma con el certificado y se envía. Si el SRI no contesta, queda
          guardada y se puede reintentar.
        </p>

        <label className="mt-4 block text-xs font-bold uppercase tracking-wide text-armazon-700">
          Punto de emisión
        </label>
        <select
          value={punto}
          onChange={(e) => setPunto(e.target.value)}
          className="mt-1.5 h-10 w-full rounded-xl border border-armazon-300 bg-white px-3 text-sm"
        >
          <option value="">Elija uno</option>
          {(puntos.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.codigo} · {p.nombre}
            </option>
          ))}
        </select>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={alCancelar}
            className="rounded-xl px-4 py-2 text-xs font-bold uppercase tracking-wide text-armazon-600 hover:bg-armazon-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={punto === '' || ocupado}
            onClick={() => alEnviar(Number(punto))}
            className="rounded-xl bg-marca-600 px-4 py-2 text-xs font-bold uppercase tracking-wide text-white hover:bg-marca-700 disabled:opacity-50"
          >
            {ocupado ? 'Enviando' : 'Enviar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmarAnulacion({
  motivo,
  alEscribir,
  alCancelar,
  alAnular,
  ocupado,
}: {
  motivo: string;
  alEscribir: (v: string) => void;
  alCancelar: () => void;
  alAnular: () => void;
  ocupado: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-armazon-950/60 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <h2 className="text-sm font-bold uppercase tracking-wide text-armazon-800">
          Anular la retención
        </h2>
        <p className="mt-1 text-[11px] uppercase leading-relaxed tracking-wide text-armazon-500">
          Queda constancia de quién la anuló y por qué. Después se puede emitir otra sobre la
          misma compra.
        </p>

        <textarea
          value={motivo}
          onChange={(e) => alEscribir(e.target.value)}
          rows={3}
          maxLength={300}
          placeholder="Por qué se anula"
          className="mt-4 w-full rounded-xl border border-armazon-300 p-3 text-sm"
        />

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={alCancelar}
            className="rounded-xl px-4 py-2 text-xs font-bold uppercase tracking-wide text-armazon-600 hover:bg-armazon-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={motivo.trim().length < 3 || ocupado}
            onClick={alAnular}
            className="rounded-xl bg-alerta px-4 py-2 text-xs font-bold uppercase tracking-wide text-white hover:opacity-90 disabled:opacity-50"
          >
            {ocupado ? 'Anulando' : 'Anular'}
          </button>
        </div>
      </div>
    </div>
  );
}
